/**
 * 탭 간 딜 변경 통지 — 서버 구성 편집기(별탭) ↔ 딜 상세 탭.
 *
 * 왜 `window.opener` 로 직접 갱신하지 않는가:
 *   opener 는 자기를 연 **한 탭**만 가리킨다. 같은 딜을 여러 탭에 띄워 둘 수
 *   있고 그 전부가 갱신돼야 하므로, 같은 오리진의 모든 탭이 받는
 *   BroadcastChannel 을 쓴다. 편집기가 남의 window 를 직접 조작하지 않는
 *   편이 결합도 면에서도 낫다.
 *
 *   단, **탭을 닫는 것**은 opener 관계가 필요하다(브라우저는 스크립트가 연
 *   창만 닫게 한다). 그래서 편집기 탭을 열 때 `'noopener'` 를 붙이지 않는다
 *   — 붙였을 때 close 가 거부돼 딜 탭이 둘이 되는 버그가 있었다.
 *
 * 지원하지 않는 브라우저에서는 조용히 no-op 이다(구독도 발행도 안 됨).
 * 그 경우 편집기 탭은 `window.close()` 실패 후 딜 URL 로 이동하는 기존
 * 폴백을 그대로 탄다.
 */

const CHANNEL = 'hcltech:deal';

export interface DealChangedMessage {
  type:   'deal-changed';
  dealId: string;
  /** 무엇이 바뀌었는지 — 지금은 로그/디버깅용. */
  reason: 'server-config-saved';
}

function open(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
  } catch {
    return null;   // 사파리 사생활 모드 등
  }
}

/** 이 딜이 다른 탭에서 바뀌었음을 알린다. 발행 후 채널은 바로 닫는다. */
export function publishDealChanged(msg: DealChangedMessage): void {
  const ch = open();
  if (!ch) return;
  try { ch.postMessage(msg); } finally { ch.close(); }
}

/**
 * 이 딜의 변경 알림을 구독한다. 반환값을 호출하면 해제된다(onUnmount 에서 필수).
 * 자기 탭이 보낸 메시지는 BroadcastChannel 규약상 받지 않으므로 자기 갱신 루프는 없다.
 */
export function subscribeDealChanged(
  dealId: string,
  onChange: (msg: DealChangedMessage) => void,
): () => void {
  const ch = open();
  if (!ch) return () => {};
  const handler = (e: MessageEvent): void => {
    const msg = e.data as DealChangedMessage | null;
    if (msg?.type !== 'deal-changed') return;
    if (String(msg.dealId) !== String(dealId)) return;   // 다른 딜 탭은 무시
    onChange(msg);
  };
  ch.addEventListener('message', handler);
  return () => { ch.removeEventListener('message', handler); ch.close(); };
}
